<template>
  <div class="motor-sidebar-card">
    <div class="card-title-label">
      <v-icon small color="rgba(255,255,255,0.55)" class="tw-mr-1">mdi-car-outline</v-icon>
      {{ checkoutMode ? 'Your cover' : 'Live premium' }}
    </div>

    <div v-if="coverLabel" class="sidebar-cover">{{ coverLabel }}</div>
    <div v-if="vehicleLine" class="sidebar-vehicle">{{ vehicleLine }}</div>

    <div class="sidebar-premium-block">
      <span class="sidebar-premium-label">Annual premium</span>
      <span class="sidebar-premium-val">KES {{ formatMoney(premium) }}</span>
    </div>
    <div v-if="ratePercent != null && isComprehensive" class="sidebar-rate">
      {{ ratePercent }}% of sum insured
    </div>

    <div v-if="checkoutMode && quoteId" class="checkout-meta">
      <div class="checkout-meta-row">
        <span>Quote ref.</span>
        <span>{{ quoteId }}</span>
      </div>
    </div>

    <template v-if="hasQuoteRef && !checkoutMode">
      <div class="sidebar-actions">
        <button type="button" class="sidebar-action-btn" @click="openEmailDialog">
          <v-icon x-small color="white" class="tw-mr-1">mdi-email-outline</v-icon>
          Email
        </button>
        <button type="button" class="sidebar-action-btn" @click="pdfDialogOpen = true">
          <v-icon x-small color="white" class="tw-mr-1">mdi-download</v-icon>
          PDF
        </button>
      </div>
    </template>

    <div v-if="!checkoutMode && canContinue" class="cta-bar">
      <button type="button" class="btn-continue" :disabled="!premium" @click="$emit('continue')">
        Continue
        <v-icon small color="white" class="tw-ml-1">mdi-arrow-right</v-icon>
      </button>
    </div>

    <!-- Email dialog -->
    <v-dialog v-model="emailOpen" max-width="420" overlay-color="#1E4276" overlay-opacity="0.55">
      <div class="eq-dialog">
        <div class="eq-header">
          <div class="eq-header-icon"><v-icon size="24" color="white">mdi-email-send-outline</v-icon></div>
          <div>
            <p class="eq-title">Email quote</p>
            <p class="eq-subtitle">{{ emailDialogSubtitle }}</p>
          </div>
          <button type="button" class="eq-close" @click="emailOpen = false"><v-icon size="18" color="white">mdi-close</v-icon></button>
        </div>
        <div class="eq-body">
          <div class="eq-field">
            <label class="eq-label">Your name on quote</label>
            <input v-model="customerName" class="eq-input" placeholder="e.g. John Kamau" type="text">
          </div>
          <div class="eq-field">
            <label class="eq-label">Email address <span class="eq-req">*</span></label>
            <input v-model="customerEmail" class="eq-input" placeholder="you@email.com" type="email">
          </div>
        </div>
        <div class="eq-footer">
          <button type="button" class="eq-btn-cancel" @click="emailOpen = false">Cancel</button>
          <button type="button" class="eq-btn-send" :disabled="!customerEmail || sending" @click="sendEmail">
            {{ sending ? 'Sending…' : 'Send quote' }}
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- PDF dialog -->
    <v-dialog v-model="pdfDialogOpen" max-width="420" overlay-color="#1E4276" overlay-opacity="0.55">
      <div class="eq-dialog">
        <div class="eq-header">
          <div class="eq-header-icon"><v-icon size="24" color="white">mdi-file-download-outline</v-icon></div>
          <div>
            <p class="eq-title">Download PDF quote</p>
            <p class="eq-subtitle">Formatted invoice — opens in a new tab</p>
          </div>
          <button type="button" class="eq-close" @click="pdfDialogOpen = false"><v-icon size="18" color="white">mdi-close</v-icon></button>
        </div>
        <div class="eq-body">
          <div class="eq-field">
            <label class="eq-label">Your name on quote</label>
            <input v-model="pdfClientName" class="eq-input" placeholder="e.g. John Kamau" type="text">
          </div>
          <div class="eq-field">
            <label class="eq-label">Email on quote</label>
            <input v-model="pdfClientEmail" class="eq-input" placeholder="you@email.com" type="email">
          </div>
          <p class="eq-hint">PDF includes vehicle, cover type, premium breakdown and benefits.</p>
        </div>
        <div class="eq-footer">
          <button type="button" class="eq-btn-cancel" @click="pdfDialogOpen = false">Cancel</button>
          <button type="button" class="eq-btn-send" :disabled="generatingPdf" @click="downloadPdf">
            <v-progress-circular v-if="generatingPdf" indeterminate size="14" width="2" color="#1E4276" class="tw-mr-2" />
            {{ generatingPdf ? 'Generating…' : 'Open PDF' }}
          </button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { MOTOR_COVER_COMPREHENSIVE } from '@/constants/motorPrivate';
import {
  buildMotorQuoteEmailHtml,
  openMotorQuotePdf,
} from '@/utils/motorQuoteDocument';
import { validateEmail } from '@/utils/paValidation';

export default {
  name: 'MotorQuoteSidebar',
  props: {
    coverLabel: { type: String, default: '' },
    vehicleLine: { type: String, default: '' },
    premium: { type: Number, default: 0 },
    rateApplied: { type: Number, default: null },
    covertypeId: { type: Number, default: null },
    quoteId: { type: [String, Number], default: '' },
    quote: { type: Object, default: () => ({}) },
    channel: { type: String, default: 'customer' },
    checkoutMode: { type: Boolean, default: false },
    canContinue: { type: Boolean, default: false },
  },
  data() {
    return {
      emailOpen: false,
      pdfDialogOpen: false,
      customerName: '',
      customerEmail: '',
      pdfClientName: '',
      pdfClientEmail: '',
      sending: false,
      generatingPdf: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticatedUser']),
    ratePercent() {
      const r = Number(this.rateApplied);
      if (Number.isNaN(r) || r <= 0) return null;
      return Number.isInteger(r) ? r : Number(r.toFixed(2));
    },
    isComprehensive() {
      return Number(this.covertypeId) === MOTOR_COVER_COMPREHENSIVE;
    },
    hasQuoteRef() {
      return !!(this.quote?.quoteId || this.quoteId);
    },
    exportQuote() {
      return {
        ...this.quote,
        quoteId: this.quote?.quoteId || this.quoteId,
        coverLabel: this.quote?.coverLabel || this.coverLabel,
        vehicleLine: this.quote?.vehicleLine || this.vehicleLine,
        premium: this.quote?.premium ?? this.premium,
        covertypeId: this.quote?.covertypeId ?? this.covertypeId,
        rateApplied: this.quote?.rateApplied ?? this.rateApplied,
      };
    },
    intermediaryLabel() {
      return this.channel === 'customer'
        ? 'Direct (Customer Portal)'
        : (this.authenticatedUser?.name || 'N/A');
    },
    emailDialogSubtitle() {
      return this.channel === 'customer'
        ? 'Send this quote to your email'
        : 'Send this quote to your customer';
    },
  },
  watch: {
    pdfDialogOpen(open) {
      if (open) this.prefillPdfFields();
    },
  },
  methods: {
    formatMoney(n) {
      return Number(n || 0).toLocaleString('en-KE');
    },
    prefillPdfFields() {
      this.pdfClientName = this.customerName || this.exportQuote.clientName || this.authenticatedUser?.name || '';
      this.pdfClientEmail = this.customerEmail || this.exportQuote.clientEmail || this.authenticatedUser?.email || '';
    },
    openEmailDialog() {
      this.customerName = this.exportQuote.clientName || this.authenticatedUser?.name || '';
      this.customerEmail = this.exportQuote.clientEmail || this.authenticatedUser?.email || '';
      this.emailOpen = true;
    },
    async downloadPdf() {
      this.generatingPdf = true;
      try {
        await openMotorQuotePdf(this.exportQuote, this.pdfClientName, {
          clientEmail: this.pdfClientEmail,
          agentName: this.intermediaryLabel,
        });
        this.pdfDialogOpen = false;
        this.pdfClientName = '';
        this.pdfClientEmail = '';
      } catch {
        this.$toast.error('Could not generate PDF');
      } finally {
        this.generatingPdf = false;
      }
    },
    async sendEmail() {
      const em = validateEmail(this.customerEmail);
      if (!em.valid) {
        this.$toast.error(em.message);
        return;
      }
      this.sending = true;
      try {
        const body = buildMotorQuoteEmailHtml(this.exportQuote, this.customerName, {
          agentName: this.intermediaryLabel,
        });
        const vehicle = this.exportQuote.vehicleLine || 'Motor';
        await axios.post('/customer/digital-notification/client-api/v1/emails', {
          to: em.normalized,
          subject: `Motor Insurance Quote — ${vehicle} | Pacis Insurance`,
          body,
        }, { _skipErrorRedirect: true });
        this.$toast.success(`Quote emailed to ${em.normalized}`);
        this.emailOpen = false;
        this.customerName = '';
        this.customerEmail = '';
      } catch {
        this.$toast.error('Failed to send email');
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style scoped>
.motor-sidebar-card {
  background: linear-gradient(180deg, #1e4276 0%, #2a5a9e 100%);
  border-radius: 14px;
  padding: 18px 16px;
  color: white;
  box-shadow: 0 4px 16px rgba(30, 66, 118, 0.2);
}
.card-title-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.85;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}
.sidebar-cover {
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 6px;
}
.sidebar-vehicle {
  font-size: 0.78rem;
  opacity: 0.85;
  margin-bottom: 14px;
  line-height: 1.4;
}
.sidebar-premium-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  border-bottom: 1px solid rgba(255, 255, 255, 0.15);
  margin-bottom: 10px;
}
.sidebar-premium-label {
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.75;
}
.sidebar-premium-val {
  font-size: 1.35rem;
  font-weight: 800;
  color: #7dd3fc;
}
.sidebar-rate {
  font-size: 0.75rem;
  opacity: 0.8;
  margin-bottom: 10px;
}
.checkout-meta {
  margin-top: 8px;
  padding-top: 8px;
  font-size: 0.72rem;
  opacity: 0.85;
}
.checkout-meta-row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}
.sidebar-actions { display: flex; gap: 6px; margin-top: 14px; }
.sidebar-action-btn {
  flex: 1;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  padding: 8px 6px;
  color: white;
  font-size: 0.68rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
}
.sidebar-action-btn:hover { background: rgba(255, 255, 255, 0.2); }
.cta-bar { margin-top: 14px; }
.btn-continue {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 11px 14px;
  border: none;
  border-radius: 10px;
  background: #00aeef;
  color: white;
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
}
.btn-continue:disabled { opacity: 0.45; cursor: not-allowed; }
.btn-continue:hover:not(:disabled) { filter: brightness(1.05); }
.eq-dialog { background: white; border-radius: 12px; overflow: hidden; }
.eq-header {
  background: linear-gradient(135deg, #1E4276, #2A5A9E);
  padding: 18px 20px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  position: relative;
}
.eq-header-icon {
  width: 40px; height: 40px; border-radius: 10px;
  background: rgba(255, 255, 255, 0.15);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.eq-title { margin: 0; font-size: 1rem; font-weight: 700; color: white; }
.eq-subtitle { margin: 2px 0 0; font-size: 0.75rem; color: rgba(255, 255, 255, 0.7); }
.eq-close {
  position: absolute; top: 14px; right: 14px;
  background: rgba(255, 255, 255, 0.15); border: none; border-radius: 50%;
  width: 28px; height: 28px; cursor: pointer; display: flex; align-items: center; justify-content: center;
}
.eq-body { padding: 20px; }
.eq-field { margin-bottom: 14px; }
.eq-label { font-size: 0.78rem; font-weight: 600; color: #64748B; display: block; margin-bottom: 6px; }
.eq-req { color: #E53E3E; }
.eq-input {
  width: 100%; padding: 10px 12px; border: 1.5px solid #E2E8F0; border-radius: 8px;
  font-size: 0.9rem; color: #1E4276; box-sizing: border-box;
}
.eq-input:focus { outline: none; border-color: #00AEEF; box-shadow: 0 0 0 3px rgba(0, 174, 239, 0.12); }
.eq-footer { padding: 0 20px 20px; display: flex; gap: 10px; justify-content: flex-end; }
.eq-btn-cancel {
  padding: 10px 18px; border-radius: 8px; border: 1px solid #E2E8F0;
  background: white; font-weight: 600; color: #64748B; cursor: pointer;
}
.eq-btn-send {
  padding: 10px 18px; border-radius: 8px; border: none;
  background: #00AEEF; font-weight: 700; color: #1E4276; cursor: pointer;
}
.eq-btn-send:disabled { opacity: 0.5; cursor: not-allowed; }
.eq-hint { font-size: 0.75rem; color: #94a3b8; margin: 0; line-height: 1.4; }

@media (max-width: 600px) {
  .sidebar-actions {
    flex-direction: column;
  }
  .sidebar-action-btn {
    min-height: 40px;
    font-size: 0.75rem;
  }
  .eq-footer {
    flex-direction: column-reverse;
    gap: 8px;
  }
  .eq-btn-cancel,
  .eq-btn-send {
    width: 100%;
    justify-content: center;
  }
}
</style>
